import UAHelper from '../utils/uaHelper';
import {
  AppPlatform,
  RichViewBridge,
  IosInterface,
  ShimmedEnv,
  AppWindow,
  PlatformType,
  IRichViewBridgeParams
} from './definitions';
import {
  getUID,
  setupOnFailureBridgeCB,
  setupOnSuccessBridgeCB
} from './utils';

export const applicationPlatform: AppPlatform = ((): AppPlatform => {
  const $W: AppWindow = window;
  const hasEnvParams: boolean = !!$W.appEnvironment;
  //copied as is from MDN
  function storageAvailable(type: string): boolean {
    let storage: any;
    try {
      storage = $W[type];
      var x = '__storage_test__';
      storage.setItem(x, x);
      storage.removeItem(x);
      return true;
    } catch (e) {
      return (
        e instanceof DOMException &&
        // everything except Firefox
        (e.code === 22 ||
          // Firefox
          e.code === 1014 ||
          // test name field too, because code might not be present
          // everything except Firefox
          e.name === 'QuotaExceededError' ||
          // Firefox
          e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
        // acknowledge QuotaExceededError only if there's something already stored
        storage &&
        storage.length !== 0
      );
    }
  }
  let shimmedEnvParams: ShimmedEnv = {};
  if (hasEnvParams) {
    shimmedEnvParams = Object.getPrototypeOf($W.appEnvironment);
  }
  const currentPlatform: string = shimmedEnvParams.platform || '';
  const UAString: string = UAHelper.getUserAgent();
  const isAndroid: boolean = UAHelper.isAndroid();
  const isIOS: boolean = UAHelper.isIOS();
  const isWindows: boolean = UAHelper.isWindows();
  const isMSite: boolean = UAHelper.isMSite();
  if (
    !UAString ||
    (UAString.toLowerCase().indexOf('fkua') < 0 &&
      currentPlatform === PlatformType.WEB)
  ) {
    shimmedEnvParams.fkUA = `${UAString} FKUA/msite/0.0.1/msite/Mobile`;
  }

  let applicationPlatform: AppPlatform = {
    isMSite,
    isAndroid,
    isIOS,
    isWindows,
    current: 'unknown',
    envShim: shimmedEnvParams,
    localStorageAvailable: storageAvailable('localStorage'),
    sessionStorageAvailable: storageAvailable('sessionStorage')
  };
  switch (true) {
    case applicationPlatform.isAndroid:
      applicationPlatform.current = PlatformType.ANDROID;
      break;
    case applicationPlatform.isIOS:
      applicationPlatform.current = PlatformType.IOS;
      break;
    case applicationPlatform.isWindows:
      applicationPlatform.current = PlatformType.WINDOWS;
      break;
    case applicationPlatform.isMSite:
      applicationPlatform.current = PlatformType.WEB;
      break;
    default:
      break;
  }

  return applicationPlatform;
})();
((globalEnv: AppWindow, platform: AppPlatform) => {
  if (platform.isIOS) {
    const addFrame = (src: string): { frame: any; id: any } => {
      let frame: any | undefined;
      let id: string | undefined;
      try {
        const frame = document.createElement('IFRAME');
        const uid = getUID();
        id = `${uid}-frame`;
        frame.style.width = 'auto';
        frame.style.height = '0px';
        frame.style.opacity = '0';
        frame.style.zIndex = '-999';
        frame.setAttribute('id', `${id}`);
        frame.setAttribute('src', src);
        document.documentElement.appendChild(frame);
      } catch (error) {
        //@ts-ignore
      }
      return { frame, id };
    };
    const constructAppURI = (
      method: string,
      args: any,
      onSuccess: (_arg: any) => void,
      onFailure: () => void
    ): string => {
      const appInterfaceUrlProtocol = 'fknative://';
      const options: IosInterface = Object.create(null);
      options.method = method;
      options.args = args;
      options.onSuccess = onSuccess;
      options.onFailure = onFailure;
      return `${appInterfaceUrlProtocol}${encodeURIComponent(
        JSON.stringify(options)
      )}`;
    };
    globalEnv.NativeInterface = function(
      method: string,
      args: any,
      onSuccess: () => void,
      onFailure: () => void
    ): void {
      const uri: string = constructAppURI(method, args, onSuccess, onFailure);
      const { frame: iFrame } = addFrame(uri);
      try {
        iFrame.parentNode.removeChild(iFrame);
      } catch (error) {
        //@ts-ignore
      }
    };
  } else if (platform.isAndroid) {
    globalEnv.NativeInterface = globalEnv.Android || false;
    globalEnv.Android &&
      globalEnv.Android.setRichView &&
      globalEnv.Android.setRichView(true);
  }
})(window, { ...applicationPlatform });

export const NativeInterface: RichViewBridge = ((
  globalEnv: AppWindow,
  platform: AppPlatform
): RichViewBridge => {
  let rvNativeInterface: any | undefined;
  let hasNativeInterface: boolean | undefined;

  if (platform.isAndroid) {
    rvNativeInterface = globalEnv.NativeInterface || globalEnv.Android;
  } else if (platform.isIOS) {
    rvNativeInterface = globalEnv.NativeInterface || globalEnv.ios;
  } else if (platform.isWindows) {
    try {
      const notifier: any | undefined = globalEnv.external;
      if (notifier) {
        const { notify }: { notify: any | undefined | unknown } = notifier;
        notify && notify('');
        rvNativeInterface = notify;
      }
    } catch (error) {
      rvNativeInterface = undefined;
    }
  } else {
    rvNativeInterface = undefined;
  }
  hasNativeInterface = !!rvNativeInterface;

  const executeBridgeCall = (
    resolve: (param?: any) => any,
    reject: (param?: any) => any,
    method: string,
    params?: any | undefined,
    isSetupCallbackRequiredForAndroid?: boolean
  ): any => {
    const genericRejection: string = `Callee ${method} on NativeInterface of ${platform.current} raised an exception`;
    const unsupportedBridgeException: string = `Operating currently on ${platform.current} platform. This doesnt have support for bridges with NativeInterface`;

    if (platform.isIOS || platform.isWindows) {
      const uid = getUID();
      const onSuccessBridgeCB = `${platform.current ||
        'iOSOrWindows'}BridgeCBFor${method || 'unknownMethod'}${uid}`;
      const onFailureBridgeCB = `onFailureOf${onSuccessBridgeCB}`;

      //setup success
      setupOnSuccessBridgeCB({
        globalEnv: globalEnv.top?.window ?? globalEnv,
        onSuccessBridgeCB,
        onFailureBridgeCB,
        resolve
      });

      if (platform.isIOS) {
        setupOnFailureBridgeCB({
          globalEnv: globalEnv.top?.window ?? globalEnv,
          onFailureBridgeCB,
          reject,
          genericRejection
        });

        const args = [method, params, onSuccessBridgeCB, onFailureBridgeCB];
        rvNativeInterface(...args);
      } else {
        const args = JSON.stringify({
          CommandType: method,
          ParamExtra: params,
          TargetMethod: onSuccessBridgeCB
        });
        rvNativeInterface(args);
      }
    } else if (platform.isAndroid) {
      if (
        !!rvNativeInterface[method] === false ||
        typeof rvNativeInterface[method] !== 'function'
      ) {
        throw new Error(
          `Unsupported method ${method} on platform ${platform.current}`
        );
      }

      const args = params;
      let result: any | undefined;

      try {
        if (isSetupCallbackRequiredForAndroid) {
          const uid = getUID();
          const onSuccessBridgeCB = `${platform.current ||
            'iOSOrWindows'}BridgeCBFor${method || 'unknownMethod'}${uid}`;
          const onFailureBridgeCB = `onFailureOf${onSuccessBridgeCB}`;

          setupOnSuccessBridgeCB({
            globalEnv: globalEnv.top?.window ?? globalEnv,
            onSuccessBridgeCB,
            onFailureBridgeCB,
            resolve
          });

          setupOnFailureBridgeCB({
            globalEnv: globalEnv.top?.window ?? globalEnv,
            onFailureBridgeCB,
            reject,
            genericRejection
          });

          return rvNativeInterface?.[method](
            onSuccessBridgeCB,
            onFailureBridgeCB
          );
        } else {
          result = params
            ? rvNativeInterface[method](args)
            : rvNativeInterface[method]();
        }
      } catch (error) {
        reject(error instanceof Error ? error.message : error);
      }
      try {
        result = JSON.parse(result);
      } catch (error) {
        //@ts-ignore
      }
      resolve(result);
    } else {
      reject(unsupportedBridgeException);
    }
  };

  const invoke = (options: IRichViewBridgeParams): Promise<any> => {
    const { method, params, isSetupCallbackRequiredForAndroid } = options;
    return new Promise((resolve, reject) => {
      try {
        if (!!method === false) {
          throw new Error('Bridge calls need a method name to be invoked');
        }
        if (hasNativeInterface === false) {
          throw new Error(
            `Bridge not defined for platform ${platform.current}`
          );
        }
      } catch (error) {
        reject(error instanceof Error ? error.message : error);
      }
      executeBridgeCall(
        resolve,
        reject,
        method,
        params,
        isSetupCallbackRequiredForAndroid
      );
    });
  };

  return {
    invoke: invoke,
    exists: hasNativeInterface
  };
})(window, { ...applicationPlatform });

export default applicationPlatform;
